/**
 * I-51 · Menú del usuario (vista nueva).
 *
 * Todo lo que tiene que ver con la persona y con cómo se comporta la app sale
 * de su nombre, en un panel con la estética de SML (no el de Clerk):
 *   · Mi cuenta: perfil y seguridad, créditos, comprar créditos.
 *   · Actividad: historial de ejecuciones.
 *   · Preferencias de la app: sonido al terminar y acciones sobre el clip (AP-3).
 *   · Conexiones: redes sociales (Buffer), MCP.
 *   · Administración (sólo admin) y Cerrar sesión.
 * La barra superior queda limpia: proyecto · Canvas/Edición · créditos · ? · usuario.
 */
import { memo, useEffect, useRef, useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import {
  User, ChevronDown, Coins, CreditCard, History, Volume2, Share2, Plug, Shield,
  LogOut, UserCog, MousePointerClick,
} from 'lucide-react';
import { useCreditsStore } from '../../store/creditsStore';
import useTimelineStore from '../../store/timelineStore';

const UserMenu = memo(({
  soundOn, onToggleSound, mcpAccess, isAdmin, activeCount = 0,
  onOpenCredits, onOpenHistory, onOpenMcp, onOpenAdmin,
}) => {
  const { user, isLoaded } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const balance = useCreditsStore((s) => s.balance);
  const openBuyModal = useCreditsStore((s) => s.openBuyModal);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const clipPill = useTimelineStore((s) => s.clipPill);
  const setClipPill = useTimelineStore((s) => s.setClipPill);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isLoaded || !user) return null;

  const run = (fn) => () => { setOpen(false); fn?.(); };
  const email = user.primaryEmailAddress?.emailAddress || user.emailAddresses?.[0]?.emailAddress || '';
  const shortName = user.firstName || email.split('@')[0] || 'Usuario';

  return (
    <div className="v2-menu-wrap v2-user" ref={ref} data-testid="user-profile">
      <button
        type="button"
        className={`v2-user-btn ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Tu cuenta y preferencias"
        data-testid="settings-menu-btn"
        data-mcp-access={mcpAccess ? '1' : '0'}
      >
        <span className="v2-user-avatar">
          {user.imageUrl ? <img src={user.imageUrl} alt="" /> : <User size={16} />}
          {activeCount > 0 && (
            <span className="v2-user-busy" title={`${activeCount} generando`} data-testid="exec-active-badge">
              {activeCount}
            </span>
          )}
        </span>
        <span className="v2-user-name">{shortName}</span>
        <ChevronDown size={13} className={open ? 'rotate' : ''} />
      </button>

      {open && (
        <div className="v2-menu is-right v2-user-menu" role="menu" data-testid="settings-menu">
          <div className="v2-user-head">
            <b>{user.fullName || shortName}</b>
            <span>{email}</span>
          </div>

          <div className="v2-menu-head">Mi cuenta</div>
          <button type="button" role="menuitem" onClick={run(onOpenCredits)} data-testid="user-menu-credits">
            <Coins size={14} /> Créditos y movimientos
            {balance != null && <span className="v2-menu-aside">{balance}</span>}
          </button>
          <button type="button" role="menuitem" className="is-accent" onClick={run(openBuyModal)} data-testid="user-menu-buy">
            <CreditCard size={14} /> Comprar créditos
          </button>
          <button type="button" role="menuitem" onClick={run(() => openUserProfile())} data-testid="user-menu-profile">
            <UserCog size={14} /> Perfil y seguridad
          </button>

          <div className="v2-menu-sep" />
          <div className="v2-menu-head">Actividad</div>
          <button type="button" role="menuitem" onClick={run(onOpenHistory)} data-testid="execution-history-btn">
            <History size={14} /> Historial de ejecuciones
            {activeCount > 0 && <span className="v2-menu-aside is-live">{activeCount} en curso</span>}
          </button>

          <div className="v2-menu-sep" />
          <div className="v2-menu-head">Preferencias de la app</div>
          <label className="v2-menu-check">
            <Volume2 size={14} />
            <span>Sonido al terminar</span>
            <input type="checkbox" checked={!!soundOn} onChange={onToggleSound} data-testid="toggle-exec-sound" />
          </label>
          <label className="v2-menu-check" title="En Edición, al tocar un clip aparece arriba un menú con Reemplazar, Cortar y Borrar">
            <MousePointerClick size={14} />
            <span>Acciones sobre el clip</span>
            <input type="checkbox" checked={!!clipPill} onChange={(e) => setClipPill(e.target.checked)} data-testid="toggle-clip-pill" />
          </label>

          <div className="v2-menu-sep" />
          <div className="v2-menu-head">Conexiones</div>
          <Link to="/settings?tab=social" className="v2-menu-link" onClick={() => setOpen(false)} data-testid="nav-social-settings">
            <Share2 size={14} /> Redes sociales (Buffer)
          </Link>
          {mcpAccess && (
            <button type="button" role="menuitem" onClick={run(onOpenMcp)} data-testid="mcp-panel-trigger">
              <Plug size={14} /> Conexiones MCP
            </button>
          )}

          {isAdmin && (
            <>
              <div className="v2-menu-sep" />
              <button type="button" role="menuitem" onClick={run(onOpenAdmin)} data-testid="admin-panel-trigger">
                <Shield size={14} /> Administración
              </button>
            </>
          )}

          <div className="v2-menu-sep" />
          <button type="button" role="menuitem" className="is-danger" onClick={run(() => signOut())} data-testid="user-menu-signout">
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
});

UserMenu.displayName = 'UserMenu';

export default UserMenu;
